import { useMemo, useState } from 'react';
import { useAppContext } from '../components/Layout';
import Drawer from '../components/Drawer';
import Spinner from '../components/Spinner';
import InfoTip from '../components/InfoTip';
import { useAssignablePeople, usePersonLabels, useVisibleContacts } from '../hooks/useScopedData';
import { commissionerPersonOf, contactToPersonFrom } from '../lib/personRefs';
import { useUnitConnections, useVisibleUnitCommissioners, useVisibleUnits } from '../hooks/useUnits';
import { isAdmin, hasCouncilFullRead, isDistrictCommissioner } from '../lib/visibility';
import { setUnitOperationalCommissioner } from '../lib/unitCommissionerActions';
import { linksByUnit, ORIGIN_LABELS, originsOf } from '../lib/unitCommissioners';
import { recName, type Unit } from '../types';

const normName = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

function lastVisitLabel(u: Unit): string {
  const d = u.commissionerActivity?.lastConnectionDate;
  return d ? `Last visit ${d}` : 'No visit on file';
}

function metricsLabel(u: Unit): string | null {
  const m = u.commissionerActivity?.metricsMet;
  return m == null ? null : `${m} metric${m === 1 ? '' : 's'} met`;
}

/** The import's suggested Commissioner, when it disagrees with who's
 *  actually assigned — null when they agree or there's nothing imported. */
function mismatchFor(u: Unit, assignedName: string): string | null {
  const suggested = u.commissionerImportSuggestion?.name;
  if (!suggested || normName(suggested) === normName(assignedName)) return null;
  return suggested;
}

export default function Commissioners() {
  const { profile, settings, districtFilter, districts } = useAppContext();
  const contactsQ = useVisibleContacts(profile);
  const peopleQ = useAssignablePeople(profile);
  const toPerson = useMemo(() => contactToPersonFrom(contactsQ.data), [contactsQ.data]);
  const unitsQ = useVisibleUnits(profile);
  // Commissioner relationships (any number per unit, from Scouting America and/or entered here). The ONE operational commissioner of a
  // unit — the person Compass uses for unit access and Action routing — is the unit's own commissioner field, shown as "Operational".
  const linksQ = useVisibleUnitCommissioners(profile);
  const linksOf = useMemo(() => linksByUnit(linksQ.data), [linksQ.data]);
  const [detailUnit, setDetailUnit] = useState<Unit | null>(null);

  const admin = isAdmin(profile);
  // A plain district member's districtFilter never actually moves off its
  // 'All' default (there's no picker for them to change it — see
  // Layout.tsx). Only admin and council-wide members have a real picker,
  // so only for them does "All" mean a genuine all-districts choice;
  // everyone else always means their own one district.
  const hasPicker = admin || hasCouncilFullRead(profile);
  const effectiveFilter = hasPicker ? districtFilter : (profile.districtId ?? districts[0]?.id ?? '');
  const isAllView = effectiveFilter === 'All';
  const dName = (id: string) => recName(settings.districts, id);

  // Whether the viewer could edit ANY row at all — drives whether the
  // "Assign to" column exists. Which specific rows they can actually touch
  // (their own district only, for a District Commissioner) is checked per
  // row below, since "All Districts" mixes districts on one screen.
  const districtCommissioner = isDistrictCommissioner(profile);
  const canEditAny = admin || districtCommissioner;
  const canEditDistrict = (districtId: string) =>
    admin || (districtCommissioner && profile.districtId === districtId);

  // Eligible for a unit assignment: a PERSON who holds Unit Commissioner, or is the District Commissioner themself
  // (a DC can personally carry units too). The server decides eligibility (canonical Position assignment or the
  // legacy roster Position), so the list is the same whether or not the person is on the roster.
  const commissioners = useMemo(
    () =>
      peopleQ.options
        .filter((o) => o.commissionerEligible && (isAllView || o.districtId === effectiveFilter))
        .sort((a, b) => dName(a.districtId).localeCompare(dName(b.districtId)) || a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [peopleQ.options, isAllView, effectiveFilter, settings.districts],
  );

  const units = useMemo(
    () =>
      unitsQ.data
        .filter((u) => isAllView || u.districtId === effectiveFilter)
        .sort((a, b) => dName(a.districtId).localeCompare(dName(b.districtId)) || a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitsQ.data, isAllView, effectiveFilter, settings.districts],
  );

  const unitsFor = (personId: string) => units.filter((u) => commissionerPersonOf(u, toPerson) === personId);
  // Units where the person is an associated commissioner but not the unit's operational one.
  const associatedFor = (personId: string) =>
    units.filter((u) => commissionerPersonOf(u, toPerson) !== personId && (linksOf.get(u.id) ?? []).some((l) => l.personId === personId));
  // People who appear on a unit only through a relationship (e.g. a Council Commissioner serving a unit) are listed too.
  const extraPeople = useMemo(() => {
    const known = new Set(commissioners.map((c) => c.id));
    const out = new Map<string, string>();
    for (const u of units) for (const l of linksOf.get(u.id) ?? []) if (!known.has(l.personId) && !out.has(l.personId)) out.set(l.personId, l.districtId);
    return [...out.entries()];
  }, [units, linksOf, commissioners]);
  const labelFor = usePersonLabels(
    [...extraPeople.map(([id]) => id), ...units.flatMap((u) => (linksOf.get(u.id) ?? []).map((l) => l.personId))],
    peopleQ.byId,
  );
  const rows = useMemo(
    () => [
      ...commissioners.map((c) => ({ id: c.id, name: c.name, districtId: c.districtId })),
      ...extraPeople.map(([id, districtId]) => ({ id, name: labelFor(id) ?? '—', districtId })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commissioners, extraPeople, labelFor],
  );
  // Unassigned also catches a "dangling" assignment — pointing at someone
  // who no longer holds Unit/District Commissioner in that district —
  // rather than hiding that unit.
  const unassigned = units.filter(
    (u) => {
      const p = commissionerPersonOf(u, toPerson);
      return !p || !commissioners.some((c) => c.id === p);
    },
  );

  // A suggested name from the import that matches a real, currently
  // eligible Commissioner in this unit's district — lets an unassigned
  // unit's suggestion become a one-click "use it" action.
  const suggestedMatch = (u: Unit) => {
    const name = u.commissionerImportSuggestion?.name;
    if (!name) return null;
    return commissioners.find((c) => c.districtId === u.districtId && normName(c.name) === normName(name)) ?? null;
  };

  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const assign = async (unit: Unit, personId: string | null) => {
    setBusyId(unit.id);
    setErr(null);
    try {
      // One server operation updates both legacy fields and the relationship record together, with the same authorization as before.
      await setUnitOperationalCommissioner(unit.id, personId);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update that assignment.');
    } finally {
      setBusyId(null);
    }
  };

  const loading = contactsQ.loading || peopleQ.loading || unitsQ.loading;
  const scopeLabel = isAllView ? 'All Districts' : dName(effectiveFilter);

  return (
    <div className="card">
      <div className="row">
        <div>
          <h2>Commissioners</h2>
          <div className="subtitle">
            {scopeLabel} — who serves which units.
            <InfoTip label="Operational and associated commissioners">
              The <b>operational commissioner</b> is the one commissioner Compass uses for a unit: they get unit-level access and are the
              person system Actions (recharter, training, advancement) are routed to. Each unit has at most one, set here by the District
              Commissioner or an administrator.
              <br />
              <br />
              <b>Associated commissioners</b> are everyone else who serves the unit, as recorded by Scouting America. They are shown for
              reference only and never change access or Action routing.
            </InfoTip>
            {!canEditAny && ' View only — the District Commissioner manages assignments.'}
            {canEditAny &&
              districtCommissioner &&
              !admin &&
              isAllView &&
              ` You can edit assignments in ${dName(profile.districtId ?? '')} only.`}
          </div>
        </div>
      </div>

      {loading && (
        <div className="empty">
          <Spinner label="Loading…" />
        </div>
      )}
      {err && <div className="err">{err}</div>}

      {!loading && (
        <>
          <div className="org-section-head" style={{ marginTop: 16 }}>
            Commissioners
          </div>
          <div className="subtitle" style={{ margin: '4px 0 8px' }}>
            <b>Operational</b> is the one commissioner Compass uses for a unit's access and Action routing. <b>Associated</b> commissioners
            serve the unit, as recorded by Scouting America, and change nothing.
            {canEditAny && (
              <>
                {' '}
                <b>Make operational</b> designates that person as the unit's operational commissioner. It replaces the current one, who stays
                listed as associated if Scouting America also lists them. Each change is recorded in the audit history.
              </>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  {isAllView && <th>District</th>}
                  <th>Commissioner</th>
                  <th>Units (operational and associated)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const assigned = unitsFor(c.id);
                  const associated = associatedFor(c.id);
                  const editableHere = canEditDistrict(c.districtId);
                  return (
                    <tr key={c.id}>
                      {isAllView && <td>{dName(c.districtId)}</td>}
                      <td>
                        {c.name}
                        {assigned.length > 0 && (
                          <span className="subtitle"> ({assigned.length} operational)</span>
                        )}
                      </td>
                      <td>
                        {assigned.length === 0 && associated.length === 0 ? (
                          <span className="subtitle">No units assigned</span>
                        ) : (
                          assigned.map((u) => {
                            const mismatch = mismatchFor(u, c.name);
                            const metrics = metricsLabel(u);
                            return (
                              <div key={u.id} className="commissioner-unit-row">
                                <button
                                  type="button"
                                  className="commissioner-unit-name"
                                  onClick={() => setDetailUnit(u)}
                                >
                                  {u.name}
                                </button>
                                <span className="subtitle" title="The commissioner Compass uses for this unit's access and Action routing">Operational</span>
                                <span className="subtitle">{lastVisitLabel(u)}</span>
                                {metrics && <span className="subtitle">{metrics}</span>}
                                {mismatch && (
                                  <span
                                    className="commissioner-mismatch"
                                    title={`Import says ${mismatch}; currently assigned to ${c.name}`}
                                  >
                                    ⚠ Import says: {mismatch}
                                  </span>
                                )}
                                {editableHere && (
                                  <button
                                    type="button"
                                    aria-label={`Remove ${u.name}`}
                                    className="commissioner-unit-remove"
                                    disabled={busyId === u.id}
                                    onClick={() => void assign(u, null)}
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                        {associated.map((u) => {
                          const link = (linksOf.get(u.id) ?? []).find((l) => l.personId === c.id);
                          const origins = link ? originsOf(link).map((o) => ORIGIN_LABELS[o]).join(' + ') : '';
                          const canPromote = canEditDistrict(u.districtId) && commissioners.some((o) => o.id === c.id && o.districtId === u.districtId);
                          return (
                            <div key={`assoc-${u.id}`} className="commissioner-unit-row">
                              <button type="button" className="commissioner-unit-name" onClick={() => setDetailUnit(u)}>
                                {u.name}
                              </button>
                              <span className="subtitle" title="Serves this unit according to the record shown; has no effect on access or Action routing">
                                Associated{origins ? ` · ${origins}` : ''}
                              </span>
                              {canPromote && (
                                <button
                                  type="button"
                                  className="btn"
                                  style={{ marginLeft: 6 }}
                                  disabled={busyId === u.id}
                                  title={`Designate ${c.name} as the operational commissioner of ${u.name}: unit access and Action routing. Replaces the current one.`}
                                  onClick={() => void assign(u, c.id)}
                                >
                                  Make operational
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={isAllView ? 3 : 2} className="empty">
                      No one holds Unit Commissioner (or District Commissioner) in {scopeLabel} yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="org-section-head" style={{ marginTop: 24 }}>
            Units without an operational commissioner ({unassigned.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  {isAllView && <th>District</th>}
                  <th>Unit</th>
                  <th>Associated commissioners</th>
                  <th>Last Visit</th>
                  <th>Metrics</th>
                  {canEditAny && <th>Designate operational</th>}
                </tr>
              </thead>
              <tbody>
                {unassigned.map((u) => {
                  const editableHere = canEditDistrict(u.districtId);
                  const options = commissioners.filter((c) => c.districtId === u.districtId);
                  const suggestion = u.commissionerImportSuggestion?.name;
                  const match = suggestedMatch(u);
                  const metrics = metricsLabel(u);
                  return (
                    <tr key={u.id}>
                      {isAllView && <td>{dName(u.districtId)}</td>}
                      <td>
                        <button
                          type="button"
                          className="commissioner-unit-name"
                          onClick={() => setDetailUnit(u)}
                        >
                          {u.name}
                        </button>
                      </td>
                      <td className="subtitle">
                        {(linksOf.get(u.id) ?? []).length === 0
                          ? '—'
                          : (linksOf.get(u.id) ?? []).map((l) => (
                              <div key={l.personId}>
                                {labelFor(l.personId) ?? '—'}
                                <span className="subtitle"> · {originsOf(l).map((o) => ORIGIN_LABELS[o]).join(' + ')}</span>
                              </div>
                            ))}
                      </td>
                      <td className="subtitle">{lastVisitLabel(u)}</td>
                      <td className="subtitle">{metrics ?? '—'}</td>
                      {canEditAny && (
                        <td>
                          {editableHere ? (
                            <>
                              <select
                                value=""
                                disabled={busyId === u.id || options.length === 0}
                                onChange={(e) => {
                                  if (e.target.value) void assign(u, e.target.value);
                                }}
                              >
                                <option value="">
                                  {suggestion ? `Designate… (suggested: ${suggestion})` : 'Designate…'}
                                </option>
                                {options.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                              {match && (
                                <button
                                  type="button"
                                  className="btn"
                                  style={{ marginLeft: 6 }}
                                  disabled={busyId === u.id}
                                  onClick={() => void assign(u, match.id)}
                                >
                                  Use suggestion
                                </button>
                              )}
                              {suggestion && !match && (
                                <div className="subtitle" style={{ marginTop: 2 }}>
                                  Suggested: {suggestion} (no matching Commissioner on file)
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="subtitle">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {unassigned.length === 0 && (
                  <tr>
                    <td colSpan={(isAllView ? 1 : 0) + 3 + (canEditAny ? 2 : 1)} className="empty">
                      Every unit in {scopeLabel} has an operational commissioner.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {detailUnit && <UnitConnectionsDrawer unit={detailUnit} onClose={() => setDetailUnit(null)} />}
    </div>
  );
}

function UnitConnectionsDrawer({ unit, onClose }: { unit: Unit; onClose: () => void }) {
  const { data, loading } = useUnitConnections(unit.id, unit.districtId);
  const activity = unit.commissionerActivity;
  const suggestion = unit.commissionerImportSuggestion;

  return (
    <Drawer title={unit.name} subtitle={<span className="subtitle">Commissioner visit history</span>} onClose={onClose}>
      <div className="uh-drawer-block">
        <h4>Current status</h4>
        <div className="subtitle">{lastVisitLabel(unit)}</div>
        {activity?.metricsMet != null && (
          <div className="subtitle">{activity.metricsMet} metric{activity.metricsMet === 1 ? '' : 's'} met</div>
        )}
        {activity?.lastAssessmentDate && (
          <div className="subtitle">
            Last assessment {activity.lastAssessmentDate}
            {activity.lastAssessmentScore != null ? ` — score ${activity.lastAssessmentScore}` : ''}
          </div>
        )}
        {suggestion?.unitExpired && (
          <div className="commissioner-mismatch" style={{ marginTop: 4 }}>
            ⚠ Import shows this unit's charter as expired{suggestion.unitExpiredDate ? ` (${suggestion.unitExpiredDate})` : ''}.
          </div>
        )}
        {suggestion?.registrationExpired && (
          <div className="commissioner-mismatch" style={{ marginTop: 4 }}>
            ⚠ Import shows the assigned position's registration as expired.
          </div>
        )}
      </div>

      <div className="uh-drawer-block">
        <h4>Visit history</h4>
        {loading && <Spinner label="Loading…" />}
        {!loading && data.length === 0 && (
          <div className="subtitle">No connections logged yet for this unit.</div>
        )}
        {data.map((c) => (
          <div key={c.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontWeight: 650 }}>
              {c.connectionDate ?? c.completeDate ?? 'Undated'} · {c.status}
            </div>
            <div className="subtitle">
              Logged by {c.loggedByName || 'unknown'} · {c.metricsMet} metric{c.metricsMet === 1 ? '' : 's'} met
            </div>
            {c.comments.map((cm, i) => (
              <div key={i} style={{ marginTop: 6 }}>
                <b style={{ fontSize: 13 }}>{cm.category}</b>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{cm.text}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Drawer>
  );
}
